import dayjs from 'dayjs';
import { DayPicker } from 'react-day-picker';
import { Text } from 'preact-i18n';

import DayButtonWithBadge from './DayButtonWithBadge';
import ReturnOpenSwitch from './ReturnOpenSwitch';

const CalendarSection = ({
  sharedDayPickerProps,
  today,
  roundWithOpenSlot,
  useOneWaySingleMode,
  startDate,
  endDate,
  dayBadgeText,
  showTodayAccentHint,
  departureAvailability,
  returnAvailability,
  activeLeg = 'departure',
  onDayWithPriceHover,
  handleRoundOpenSlotChange,
  handleOneWaySelect,
  handleRangeChange,
  handleTodayClick,
  todayActionDisabled,
  onCloseClick,
  showOpenTicket,
  departureIsOpen,
  returnIsOpen,
  handleToggleOpenReturn,
  flatVariant,
  useAvailability,
  discountOpenTicket,
}) => {
  const useSingleMode = roundWithOpenSlot || useOneWaySingleMode;

  return (
    <div className="udp-calendar-wrap">
      <DayPicker
        {...sharedDayPickerProps}
        today={today}
        components={{
          ...sharedDayPickerProps.components,
          DayButton: ({ day, modifiers, ...props }) => {
            const dateKey = dayjs(day.date).format('YYYY-MM-DD');
            const isRangeStart =
              modifiers.range_start || (startDate && dayjs(day.date).isSame(startDate, 'day'));
            const isRangeEnd =
              modifiers.range_end || (endDate && dayjs(day.date).isSame(endDate, 'day'));
            const sourceLeg = isRangeStart
              ? 'departure'
              : isRangeEnd
                ? 'return'
                : activeLeg;
            const sourceAvailability = sourceLeg === 'return'
              ? returnAvailability
              : departureAvailability;
            const dayData = sourceAvailability[dateKey] ?? null;
            return (
              <DayButtonWithBadge
                {...props}
                day={day}
                modifiers={modifiers}
                badgeText={dayBadgeText}
                showTodayAccentHint={showTodayAccentHint}
                dayData={dayData}
                useAvailability={useAvailability}
                onDayWithPriceHover={
                  onDayWithPriceHover
                    ? dayDataArg => onDayWithPriceHover(dateKey, dayDataArg, sourceLeg)
                    : undefined
                }
              />
            );
          },
        }}
        mode={useSingleMode ? 'single' : 'range'}
        min={!useSingleMode ? 0 : undefined}
        selected={
          useSingleMode
            ? startDate || undefined
            : startDate || endDate
              ? endDate
                ? { from: startDate, to: endDate }
                : { from: startDate }
              : undefined
        }
        onSelect={
          roundWithOpenSlot
            ? handleRoundOpenSlotChange
            : useOneWaySingleMode
              ? handleOneWaySelect
              : handleRangeChange
        }
        resetOnSelect
      />

      <div className="udp-footer">
        {/*
         * Disabled rather than hidden while the first days are blocked. Today is not selectable, so
         * the shortcut would jump to a different date than its label says — but a greyed out button
         * says the shortcut exists and does not apply, which is what pickadate does in the legacy
         * picker, and it keeps the footer's two children so "Cerrar" stays at its own end.
         */}
        <button
          type="button"
          className="udp-footer-btn udp-footer-btn--today"
          onClick={handleTodayClick}
          disabled={todayActionDisabled}
        >
          <Text id="label.today" />
        </button>
        <button type="button" className="udp-footer-btn" onClick={onCloseClick}>
          <Text id="button.close" />
        </button>
      </div>

      {showOpenTicket && (startDate || departureIsOpen) && (
        <ReturnOpenSwitch
          returnIsOpen={returnIsOpen}
          handleToggleOpenReturn={handleToggleOpenReturn}
          flatVariant={flatVariant}
          discountOpenTicket={discountOpenTicket}
        />
      )}
    </div>
  );
};

export default CalendarSection;
